import React, { useState } from 'react';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Card,
  Form,
} from 'react-bootstrap';
import { FilterList, Clear } from '@mui/icons-material';
import './OrderRequestFilterComponent.css';

const STATUS_OPTIONS = [
  { value: 'REQUESTED', label: 'Open (REQUESTED)' },
  { value: 'FULFILLED', label: 'Fulfilled' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/**
 * Collapsible filter panel for the order requests inbox (Projects-style).
 *
 * @param {Object} props
 * @param {Object} props.filters
 * @param {function} props.setFilters
 * @param {function} props.onApplyFilters
 * @param {function} props.onClearFilters
 */
const OrderRequestFilterComponent = ({
  filters,
  setFilters,
  onApplyFilters,
  onClearFilters,
}) => {
  const [activeKey, setActiveKey] = useState('0');

  const handleFilterChange = (filterKey, value) => {
    const next = { ...filters, [filterKey]: value };
    setFilters(next);
    onApplyFilters({ [filterKey]: value });
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'REQUESTED',
      productId: '',
      createdAfter: '',
      createdBefore: '',
    });
    onClearFilters();
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'status') return value !== '' && value !== 'REQUESTED';
    return value !== '' && value != null;
  });

  return (
    <Card className="mb-3 order-request-filter-card">
      <Card.Header className="order-request-filter-header">
        <div className="order-request-filter-title">
          <FilterList className="order-request-filter-icon" />
          <strong>Order request filters</strong>
          {hasActiveFilters && (
            <span className="active-filters-indicator">
              {
                Object.entries(filters).filter(([key, value]) => {
                  if (key === 'status') {
                    return value !== '' && value !== 'REQUESTED';
                  }
                  return value !== '' && value != null;
                }).length
              }
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClearFilters}
            className="clear-filters-btn"
          >
            <Clear className="me-1" />
            Clear all
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        <Accordion activeKey={activeKey} onSelect={key => setActiveKey(key)}>
          <AccordionItem eventKey="0">
            <AccordionHeader>Status</AccordionHeader>
            <AccordionBody>
              <Form.Group className="order-request-status-options">
                <Form.Check
                  type="radio"
                  label="All statuses"
                  name="orderRequestStatus"
                  checked={filters.status === ''}
                  onChange={() => handleFilterChange('status', '')}
                />
                {STATUS_OPTIONS.map(opt => (
                  <Form.Check
                    key={opt.value}
                    type="radio"
                    label={opt.label}
                    name="orderRequestStatus"
                    checked={filters.status === opt.value}
                    onChange={() => handleFilterChange('status', opt.value)}
                  />
                ))}
              </Form.Group>
            </AccordionBody>
          </AccordionItem>

          <AccordionItem eventKey="1">
            <AccordionHeader>Product id</AccordionHeader>
            <AccordionBody>
              <Form.Control
                type="number"
                min={1}
                value={filters.productId}
                placeholder="Filter by product id"
                onChange={e =>
                  setFilters(prev => ({ ...prev, productId: e.target.value }))
                }
                onBlur={e => handleFilterChange('productId', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleFilterChange('productId', e.target.value);
                  }
                }}
              />
            </AccordionBody>
          </AccordionItem>

          <AccordionItem eventKey="2">
            <AccordionHeader>Created date range</AccordionHeader>
            <AccordionBody>
              <Form.Group className="mb-2">
                <Form.Label>Created after</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={filters.createdAfter}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      createdAfter: e.target.value,
                    }))
                  }
                  onBlur={e =>
                    handleFilterChange('createdAfter', e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Created before</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={filters.createdBefore}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      createdBefore: e.target.value,
                    }))
                  }
                  onBlur={e =>
                    handleFilterChange('createdBefore', e.target.value)
                  }
                />
              </Form.Group>
            </AccordionBody>
          </AccordionItem>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

export default OrderRequestFilterComponent;
